import { Modal } from '@fluentui/react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { AppObject } from '../AppCard';
import * as microsoftTeams from '@microsoft/teams-js';
import { Tag } from '../Tag';
import {
  AppModalSubscription,
  AppModalContent,
  AppModalDescription,
  AppModalHeader,
  modalStyles,
  AppPlanCard,
  AppLinkContainer,
  AppPlansContainer,
  AppModalDemo,
} from './styles';
import { ShareFat, X } from '@phosphor-icons/react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper';
import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import { Spinner } from '@fluentui/react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { callFunction } from '../../lib/Helpers';
import { People } from '@microsoft/mgt-react';
import { monetization, pages } from '@microsoft/teams-js';
import { TeamsFxContext } from '../../Context';

interface AppModalProps {
  isOpen: boolean;
  closeModal: () => void;
  app: AppObject | undefined;
  admins: string[];
  plans: any | undefined;
  tenantId: string;
  isOwner: boolean;
  themeName: string;
  isMobile?: boolean;
}

interface ICarouselItem {
  key: string;
  id: string;
  content: JSX.Element;
}

export const AppModal = ({
  app,
  admins,
  isOpen,
  closeModal,
  plans,
  isOwner,
  themeName,
  isMobile,
}: AppModalProps) => {
  const appInsights = useAppInsightsContext();

  const [context, setContext] = useState<microsoftTeams.app.Context>();
  microsoftTeams.app.getContext().then((context) => setContext(context));

  const { teamsUserCredential } = useContext(TeamsFxContext);

  const [requestDeepLink, setRequestDeepLink] = useState<string>('');
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [adminsEmails, setAdminsEmails] = useState<string[]>([]);

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  function requestAccess() {
    process.env.REACT_APP_APP_INSIGHTS !== '' &&
      app &&
      appInsights.trackEvent({
        name: 'Request License Clicked',
        properties: { cardName: app?.name },
      });

    //setIsRequestLoading(true);
    if (context && context.app.host.name === 'Teams') {
      microsoftTeams.app.openLink(requestDeepLink);
    } else {
      window.open(requestDeepLink);
    }
    //window.open(requestDeepLink);
  }

  function goToApp() {
    const insights = process.env.REACT_APP_APP_INSIGHTS !== '' && app;

    if (pages.currentApp.isSupported()) {
      pages.currentApp
        .navigateTo({ pageId: 'appstab' })
        .then(() => {
          insights &&
            appInsights.trackEvent({
              name: 'Go to App Clicked',
              properties: { cardName: app.name },
            });
        })
        .catch((error) => {
          insights &&
            appInsights.trackEvent({
              name: 'Go to App Error',
              properties: { cardName: app.name, error: error },
            });
        });
    } else {
      pages
        .navigateToApp({
          appId: process.env.REACT_APP_TEAMS_APPID!,
          pageId: 'appstab',
        })
        .then(() => {
          insights &&
            appInsights.trackEvent({
              name: 'Go to App Clicked',
              properties: { cardName: app.name },
            });
        })
        .catch((error) => {
          insights &&
            appInsights.trackEvent({
              name: 'Go to App Error',
              properties: { cardName: app.name, error: error },
            });
        });
    }
  }

  // Load Carousel Items
  const [carouselItems, setCarouselItems] = useState<ReactNode[]>([]);

  useEffect(() => {
    if (app) {
      const items = app.pictures.map((item, index) => (
        <img className="carouselItemImage" src={item} alt={`pic${index}`} />
      ));

      setCarouselItems(items);
    }
  }, [app]);

  useEffect(() => {
    const promises: Promise<any>[] = [];

    if (admins.length > 0) {
      admins.forEach((item) => {
        const userPromise = callFunction(
          true,
          'getUserProfile?{0}&uid=' + item,
          undefined,
          false,
          teamsUserCredential
        ).catch((error) => {
          if (process.env.REACT_APP_APP_INSIGHTS !== '') {
            appInsights.trackException({
              exception: error,
              properties: {
                component: 'AppCard',
                location: 'onGetUserDataFromGraphUseEffect',
              },
            });
          }
        });

        promises.push(userPromise);
      });

      Promise.all(promises).then((users) => {
        const emails = users.map((user) => user.mail);
        setAdminsEmails(emails);

        const deeplink: string = encodeURI(
          'https://teams.microsoft.com/l/chat/0/0?users=' +
            emails.join(',') +
            '&topicName=Request access to BitFractal&message=Hi, I need access to BitFractal ' +
            app?.name +
            ' app and I hope you can help me with it, thanks.'
        );
        setRequestDeepLink(deeplink);
        setIsRequestLoading(false);
      });
    }
  }, [admins, app?.name, teamsUserCredential]);

  const openPurchaseExperience = () => {
    if (process.env.REACT_APP_APP_INSIGHTS !== '') {
      appInsights.trackEvent({ name: 'Upgrade from banner clicked' });
      if (monetization.isSupported()) {
        monetization
          .openPurchaseExperience()
          .catch(() => window.open(encodeURI('https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice')));
      } else {
        window.open(encodeURI('https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'));
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={closeModal}
      isBlocking={false}
      containerClassName={modalStyles.container}
    >
      <AppModalContent>
        {app && (
          <>
            <AppModalHeader>
              <h1>{app.name}</h1>
              <h2>{app.group}</h2>
              <Tag content={app.tag} color={app.tagColor} variant="outlined" />
              <button className="closeModal" onClick={() => closeModal()}>
                <X aria-label="Close popup modal" size={24} />
              </button>
            </AppModalHeader>
            <AppModalDescription>
              <p>{app.longdescription}</p>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                navigation={true}
                pagination={{ clickable: true }}
                loop
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
              >
                {carouselItems.map((item, index) => (
                  <SwiperSlide key={`app-modal-pic-${app.id}-${index}`}>
                    {item}
                  </SwiperSlide>
                ))}
              </Swiper>
            </AppModalDescription>
            <AppModalSubscription>
              {plans ? (
                plans.plans.length === 0 ? (
                  <span>This App is not available yet!</span>
                ) : (
                  <>
                    {!isOwner && admins.length > 0 && (
                      <AppLinkContainer>
                        <span>
                          Need access to this App? The following users own a
                          subscription to it:
                        </span>
                        <div className="admins">
                          <People
                            showMax={25}
                            showPresence
                            userIds={admins}
                            className={
                              themeName !== 'default' ? 'mgt-dark' : ''
                            }
                          />
                        </div>
                        {requestDeepLink !== '' ? (
                          <div style={{ display: 'flex', gap: '16px' }}>
                            <button onClick={() => requestAccess()}>
                              Request <ShareFat weight="duotone" />
                            </button>
                            {isRequestLoading && <Spinner />}
                          </div>
                        ) : (
                          <Spinner />
                        )}
                      </AppLinkContainer>
                    )}
                    {!isMobile && isOwner && (
                      <AppLinkContainer>
                        <span>
                          Congratulations, you already have acess to this app!
                        </span>
                        {context?.app.host.name === 'Teams' && (
                          <button onClick={() => goToApp()}>
                            Go to App <ShareFat weight="duotone" />
                          </button>
                        )}
                      </AppLinkContainer>
                    )}

                    {!isMobile && (
                      <>
                        <span>
                          This App is available in the following plans:
                        </span>
                        <AppPlansContainer>
                          {plans.plans.map((plan: any) => (
                            <AppPlanCard
                              onClick={() => openPurchaseExperience()}
                            >
                              <h1>{plan.displayName}</h1>
                              <p>{plan.description}</p>
                              <button>Subscribe</button>
                            </AppPlanCard>
                          ))}
                        </AppPlansContainer>
                      </>
                    )}
                  </>
                )
              ) : (
                !isMobile && (
                  <AppPlansContainer>
                    <Spinner size={3} label="Loading plans..." />
                  </AppPlansContainer>
                )
              )}
            </AppModalSubscription>
            {app.demo && (
              <AppModalDemo>
                <p>Want to know more? Check this app's demo video below:</p>
                <div className="videoContainer">
                  <div
                    className={`${
                      isIframeLoaded ? 'loaded ' : ''
                    } iframeWrapper`}
                  >
                    <img
                      src="https://app.bitfractal.com/embedded/maxresdefault.jpg"
                      className="thumbnail"
                      alt=""
                    />
                    <iframe
                      title="BitFractal Readiness Video"
                      src={app.demo}
                      allow={'autoplay'}
                      allowFullScreen
                      // height={200}
                      // width={354}
                      onLoad={() => setIsIframeLoaded(true)}
                    />
                  </div>
                </div>
              </AppModalDemo>
            )}
          </>
        )}
      </AppModalContent>
    </Modal>
  );
};
